import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  ActionSheetIOS,
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  TextInput,
  useColorScheme,
  useWindowDimensions,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Portal } from '@gorhom/portal';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { scheduleIosUiPresent } from '@/utils/scheduleIosUiPresent';
import { useIosOverlayPresent } from '@/utils/useIosOverlayPresent';
import { useInlineSelectPicker, useSelectFieldInsideModal } from '@/components/ui/SelectFieldModalContext';
import { InModalOverlay, useIosInModalOverlay } from '@/components/ui/InModalOverlay';
import { Button } from './Button';
import { Caption, ThemedText } from './Text';

export const SELECT_PICKER_PORTAL_HOST = 'select-picker';

export type SelectOption = {
  value: string;
  label: string;
  type?: 'option' | 'section';
  meta?: string;
  disabled?: boolean;
};

/** Above this, UIKit action sheets degrade or fail silently when invoked from nested RN Modals — use searchable sheet instead. */
const IOS_NESTED_ACTION_SHEET_MAX_OPTIONS = 24;

export type SelectFieldProps = {
  label?: string;
  placeholder?: string;
  value?: string;
  options: ReadonlyArray<SelectOption>;
  onValueChange: (value: string) => void;
  helperText?: string;
  errorText?: string;
  searchable?: boolean;
  allowClear?: boolean;
  /**
   * Use ActionSheetIOS on iOS (nested inside another Modal RN cannot reliably stack modals everywhere).
   * On Android when nested we still use this component's Modal-based sheet instead of Alert, which truncates multi-option menus.
   */
  avoidNestedModal?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function SelectField({
  label,
  placeholder = 'Select an option',
  value,
  options,
  onValueChange,
  helperText,
  errorText,
  searchable = true,
  allowClear = false,
  avoidNestedModal = false,
  style,
}: SelectFieldProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const colorScheme = useColorScheme();
  const isInsideModal = useSelectFieldInsideModal();
  const useInlinePicker = useInlineSelectPicker();
  const useInModalOverlay = useIosInModalOverlay() && !useInlinePicker;
  const effectiveAvoidNestedModal = avoidNestedModal || isInsideModal;
  const { height: windowHeight } = useWindowDimensions();
  /** Pixel cap: Android treats percentage max-heights oddly inside Modal + KeyboardAvoidingView chains. */
  const pickerSheetMaxHeight = Math.min(Math.round(windowHeight * 0.82), Math.round(windowHeight - insets.top - 12));
  const sheetChromeApprox =
    theme.spacing(2) * 2 +
    theme.spacing(1.5) * 4 +
    24 +
    (searchable ? 52 : 0) +
    56;
  const listAreaMaxHeight = Math.max(
    160,
    Math.min(Math.round(windowHeight * 0.52), pickerSheetMaxHeight - sheetChromeApprox),
  );
  const inlineListMaxHeight = Math.min(280, Math.max(140, Math.round(windowHeight * 0.32)));

  const [pickerRequested, setPickerRequested] = useState(false);
  const overlayVisible = useIosOverlayPresent(pickerRequested);
  const modalVisible = useInlinePicker ? pickerRequested : overlayVisible;
  const [query, setQuery] = useState('');
  const previousValueRef = useRef<string | undefined>(value);
  const presentCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      presentCleanupRef.current?.();
      presentCleanupRef.current = null;
    };
  }, []);

  // Auto-close modal after 10 seconds to prevent stuck modals
  useEffect(() => {
    if (modalVisible) {
      const timeout = setTimeout(() => {
        console.warn('[SelectField] Auto-closing modal after 10s timeout');
        setPickerRequested(false);
        setQuery('');
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [modalVisible]);

  // Close modal when value changes externally (e.g., trip selection changed programmatically)
  useEffect(() => {
    if (pickerRequested && value !== undefined && previousValueRef.current !== value) {
      setPickerRequested(false);
      setQuery('');
    }
    previousValueRef.current = value;
  }, [value, pickerRequested]);

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

  const filteredOptions = useMemo(() => {
    if (!query.trim()) {
      return options;
    }

    const normalizedQuery = query.trim().toLowerCase();
    return options.filter((option) => {
      if (option.type && option.type !== 'option') {
        return false;
      }
      const labelMatch = option.label.toLowerCase().includes(normalizedQuery);
      const metaMatch = option.meta?.toLowerCase().includes(normalizedQuery);
      return labelMatch || metaMatch;
    });
  }, [options, query]);

  const closeModal = useCallback(() => {
    presentCleanupRef.current?.();
    presentCleanupRef.current = null;
    setPickerRequested(false);
    setQuery('');
  }, []);

  const pickableOptions = useMemo(
    () => options.filter((o) => o.type !== 'section' && !o.disabled),
    [options],
  );

  /** Large lists inside another Modal need the bottom-sheet Modal, not ActionSheetIOS. */
  const useModalPickerIosNested =
    effectiveAvoidNestedModal &&
    Platform.OS === 'ios' &&
    pickableOptions.length > IOS_NESTED_ACTION_SHEET_MAX_OPTIONS;

  /** iOS+nested Modal still uses ActionSheetIOS for tiny lists only; searchable sheet mounts when using Modal picker. */
  const renderPickerModal =
    !(effectiveAvoidNestedModal && Platform.OS === 'ios') || useModalPickerIosNested;

  const presentModalPicker = useCallback(() => {
    presentCleanupRef.current?.();
    if (useInlinePicker || useInModalOverlay) {
      setPickerRequested(true);
      return;
    }
    presentCleanupRef.current = scheduleIosUiPresent(() => {
      setPickerRequested(true);
    });
  }, [useInlinePicker, useInModalOverlay]);

  const openIosActionSheet = useCallback(() => {
    const labelForPicker = label ?? placeholder ?? 'Select';
    const sheetOptions = [...pickableOptions.map((o) => o.label), 'Cancel'];
    const cancelIndex = sheetOptions.length - 1;
    presentCleanupRef.current?.();
    presentCleanupRef.current = scheduleIosUiPresent(
      () => {
        ActionSheetIOS.showActionSheetWithOptions(
          {
            title: labelForPicker,
            options: sheetOptions,
            cancelButtonIndex: cancelIndex,
            userInterfaceStyle: colorScheme === 'dark' ? 'dark' : 'light',
          },
          (buttonIndex) => {
            if (buttonIndex === cancelIndex) return;
            const picked = pickableOptions[buttonIndex];
            if (picked) onValueChange(picked.value);
          },
        );
      },
      { retries: false },
    );
  }, [colorScheme, label, onValueChange, pickableOptions, placeholder]);

  const openPicker = useCallback(() => {
    Keyboard.dismiss();

    /** Web always uses the searchable sheet (never window.prompt). */
    if (Platform.OS === 'web') {
      presentModalPicker();
      return;
    }

    /** Nested: iOS — ActionSheet only for short lists (currency etc. blows past UIKit limits). */
    if (effectiveAvoidNestedModal && Platform.OS === 'ios') {
      if (!useModalPickerIosNested) {
        openIosActionSheet();
        return;
      }
    }

    presentModalPicker();
  }, [effectiveAvoidNestedModal, openIosActionSheet, presentModalPicker, useModalPickerIosNested]);

  const handleSelect = useCallback((nextValue: string) => {
    onValueChange(nextValue);
    closeModal();
  }, [closeModal, onValueChange]);

  const renderItemSeparator = useCallback(
    () => <View style={{ height: 1, backgroundColor: theme.colors.border }} />,
    [theme.colors.border],
  );

  const renderEmptyState = useCallback(
    () => <Caption color={theme.colors.textSecondary}>No matches found.</Caption>,
    [theme.colors.textSecondary],
  );

  const renderOptionItem = useCallback(
    ({ item }: { item: SelectOption }) => {
      if (item.type === 'section') {
        return (
          <View
            style={{
              paddingVertical: theme.spacing(0.75),
              paddingHorizontal: theme.spacing(1),
              backgroundColor: theme.colors.background,
            }}
          >
            <Caption
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: theme.colors.textSecondary,
                fontWeight: '600',
              }}
            >
              {item.label}
            </Caption>
          </View>
        );
      }

      const isSelected = item.value === value;
      const isDisabled = !!item.disabled;
      return (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={item.label}
          accessibilityHint={
            isSelected
              ? 'Currently selected. Tap to keep this selection.'
              : isDisabled
                ? 'This option is read-only.'
                : 'Tap to select this option'
          }
          accessibilityState={{ selected: !!isSelected, disabled: isDisabled }}
          disabled={isDisabled}
          onPress={() => {
            Keyboard.dismiss();
            handleSelect(item.value);
          }}
          style={({ pressed }) => [
            {
              paddingVertical: theme.spacing(1.5),
              paddingHorizontal: theme.spacing(1),
              borderRadius: theme.radii.md,
              backgroundColor:
                pressed || isSelected ? theme.colors.background : theme.colors.surface,
              opacity: isDisabled ? 0.5 : 1,
              minHeight: 44,
            },
          ]}
        >
          <ThemedText variant="bodyStrong" color={isSelected ? theme.colors.primary : theme.colors.text}>
            {item.label}
          </ThemedText>
          {item.meta && (
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
              {item.meta}
            </Caption>
          )}
          {isSelected && (
            <Caption color={theme.colors.primary}>Selected</Caption>
          )}
        </Pressable>
      );
    },
    [handleSelect, theme, value],
  );

  const borderColor = errorText
    ? theme.colors.error
    : modalVisible
      ? theme.colors.primary
      : theme.colors.border;

  const labelColor = errorText
    ? theme.colors.error
    : modalVisible
      ? theme.colors.primary
      : theme.colors.textSecondary;

  const displayLabel = selectedOption?.label ?? value ?? '';
  const displayColor = displayLabel ? theme.colors.text : theme.colors.textSecondary;

  const pickerListMaxHeight = useInlinePicker ? inlineListMaxHeight : listAreaMaxHeight;

  const pickerSheetContent = (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 24 : 0}
      style={{ width: '100%' }}
    >
      <Pressable
        onPress={(e) => {
          e.stopPropagation();
        }}
        style={{
          backgroundColor: theme.colors.surface,
          ...(useInlinePicker
            ? {
                marginTop: theme.spacing(1),
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                borderRadius: theme.radii.lg,
                maxHeight: inlineListMaxHeight + sheetChromeApprox,
              }
            : {
                borderTopLeftRadius: theme.radii.xl,
                borderTopRightRadius: theme.radii.xl,
                maxHeight: pickerSheetMaxHeight,
              }),
          paddingHorizontal: theme.spacing(2),
          paddingTop: theme.spacing(2),
          paddingBottom: useInlinePicker
            ? theme.spacing(2)
            : Math.max(theme.spacing(2), insets.bottom + theme.spacing(0.5)),
          gap: theme.spacing(2),
          width: '100%',
          overflow: 'hidden',
          flexShrink: 1,
        }}
      >
        <View style={{ gap: theme.spacing(1) }} pointerEvents="box-none">
          <ThemedText variant="title" color={theme.colors.text}>
            {label ?? 'Select'}
          </ThemedText>
          {searchable && (
            <TextInput
              {...(Platform.OS === 'web'
                ? {
                    id: label
                      ? `${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}-search`
                      : 'select-search',
                    name: label
                      ? `${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}-search`
                      : 'select-search',
                  }
                : {})}
              value={query}
              onChangeText={setQuery}
              placeholder="Search..."
              placeholderTextColor={theme.colors.textSecondary}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="off"
              textContentType="none"
              accessibilityLabel="Search options"
              accessibilityHint="Type to filter the list of available options"
              style={{
                borderWidth: 1,
                borderColor: theme.colors.border,
                borderRadius: theme.radii.md,
                paddingHorizontal: theme.spacing(2),
                paddingVertical: theme.spacing(1.5),
                backgroundColor: theme.colors.surface,
                color: theme.colors.text,
                fontSize: 16,
                minHeight: 44,
              }}
            />
          )}
        </View>

        <FlatList
          data={filteredOptions}
          keyExtractor={(item, index) =>
            item.type === 'section' ? `sec-${index}-${item.label}` : `${item.value}-${index}`
          }
          style={{ flexGrow: 0, maxHeight: pickerListMaxHeight }}
          nestedScrollEnabled
          keyboardShouldPersistTaps="always"
          ItemSeparatorComponent={renderItemSeparator}
          ListEmptyComponent={renderEmptyState}
          renderItem={renderOptionItem}
        />

        <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
          {allowClear && value && (
            <Button
              label="Clear selection"
              variant="ghost"
              onPress={() => handleSelect('')}
              style={{ flex: 1 }}
            />
          )}
          <Button
            label="Done"
            variant="secondary"
            onPress={() => {
              Keyboard.dismiss();
              closeModal();
            }}
            style={{ flex: 1 }}
          />
        </View>
      </Pressable>
    </KeyboardAvoidingView>
  );

  const pickerOverlay = renderPickerModal && !useInlinePicker ? (
    useInModalOverlay ? (
      <InModalOverlay visible={modalVisible} onClose={closeModal} variant="bottom">
        {pickerSheetContent}
      </InModalOverlay>
    ) : (
      <Modal
        animationType="slide"
        visible={modalVisible}
        transparent
        onRequestClose={closeModal}
        presentationStyle="overFullScreen"
        statusBarTranslucent={Platform.OS === 'android'}
        onDismiss={closeModal}
      >
        <Pressable
          style={[
            {
              flex: 1,
              backgroundColor: theme.colors.overlay,
              justifyContent: 'flex-end',
            },
            Platform.OS === 'web' && {
              position: 'fixed' as unknown as 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10000,
            },
          ]}
          onPress={closeModal}
        >
          {pickerSheetContent}
        </Pressable>
      </Modal>
    )
  ) : null;

  const shouldPortalPicker =
    !useInlinePicker && !useInModalOverlay && (Platform.OS === 'ios' || Platform.OS === 'web');

  return (
    <View style={[{ gap: theme.spacing(0.75) }, style]}>
      {label && (
        <ThemedText variant="bodyStrong" color={labelColor}>
          {label}
        </ThemedText>
      )}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label ? `${label}. ${selectedOption?.label || placeholder}` : (selectedOption?.label || placeholder || 'Select option')}
        accessibilityHint={`Opens selection menu. ${errorText ? `Error: ${errorText}` : helperText || 'Tap to choose an option'}`}
        accessibilityState={{ disabled: false }}
        onPress={openPicker}
        style={({ pressed }) => [
          {
            borderWidth: 1,
            borderColor,
            borderRadius: theme.radii.md,
            paddingHorizontal: theme.spacing(2),
            paddingVertical: theme.spacing(1.5),
            backgroundColor: theme.colors.surface,
            opacity: pressed ? 0.85 : 1,
            minHeight: 44,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
          },
        ]}
      >
        <ThemedText
          variant="body"
          color={displayLabel ? displayColor : theme.colors.textSecondary}
          style={{ flex: 1, minWidth: 0 }}
          numberOfLines={2}
        >
          {displayLabel || placeholder}
        </ThemedText>
        <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
      </Pressable>

      {(errorText || helperText) && (
        <Caption color={errorText ? theme.colors.error : theme.colors.textSecondary}>
          {errorText || helperText}
        </Caption>
      )}

      {useInlinePicker && modalVisible && pickerSheetContent}

      {pickerOverlay &&
        (shouldPortalPicker ? (
          <Portal hostName={SELECT_PICKER_PORTAL_HOST}>{pickerOverlay}</Portal>
        ) : (
          pickerOverlay
        ))}
    </View>
  );
}
